import { Product, Category } from '../types';
import { BlogArticle } from '../types/blog';
import { escapeXml } from './sitemapSync';
import {
  getProductLocalizedTitle,
  getProductLocalizedDescription,
  getProductArabicSlug,
  getCategoryLocalizedName,
  getCategoryArabicSlug
} from './localization';

export interface RssSyncStats {
  totalItems: number;
  productCount: number;
  categoryCount: number;
  blogPostCount: number;
  categoriesWithProducts: {
    id: string;
    name: string;
    slug: string;
    count: number;
  }[];
}

export interface RssHealthCheck {
  id: string;
  label: string;
  passed: boolean;
  detail: string;
}

export interface RssSyncResult extends RssSyncStats {
  success: boolean;
  message: string;
  bytes: number;
  timestamp: string;
  xml: string;
  xmlAr?: string;
  feedPageUrl: string;
  feedXmlUrl: string;
  feedXmlArUrl?: string;
  healthChecks: RssHealthCheck[];
}

export interface RssGenerationOptions {
  baseUrl?: string;
  categoryFilter?: string; // 'all' | 'blog' | categoryId/slug
  includeProducts?: boolean;
  includeBlogPosts?: boolean;
}

/**
 * Generates a valid RFC-822 date string required by RSS 2.0 specification
 */
function toRfc822Date(dateStr?: string): string {
  if (!dateStr) return new Date().toUTCString();
  const parsed = new Date(dateStr);
  if (isNaN(parsed.getTime())) return new Date().toUTCString();
  return parsed.toUTCString();
}

/**
 * Strips HTML tags for clean plain-text RSS descriptions
 */
function stripHtmlTags(raw: string = ''): string {
  return raw
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Generates an RSS 2.0 compliant XML feed containing all active products grouped per category and published blog posts
 */
export function generateRssXml(
  products: Product[],
  categories: Category[],
  blogArticles: BlogArticle[],
  options: RssGenerationOptions = {}
): { xml: string; stats: RssSyncStats; healthChecks: RssHealthCheck[] } {
  const cleanBase = (options.baseUrl || 'https://coveristic.net').trim().replace(/\/+$/, '');
  const categoryFilter = (options.categoryFilter || 'all').toLowerCase();
  const includeProducts = options.includeProducts !== false && categoryFilter !== 'blog';
  const includeBlogPosts =
    options.includeBlogPosts !== false && (categoryFilter === 'all' || categoryFilter === 'blog');

  const nowRfc = new Date().toUTCString();
  const activeCategories = (categories || []).filter(c => c && c.active !== false);
  const activeProducts = (products || []).filter(p => p && !p.inRecycleBin && !p.unlisted);
  const publishedBlogs = (blogArticles || []).filter(b => b && b.status === 'published');

  const categoriesWithProducts: RssSyncStats['categoriesWithProducts'] = [];
  const itemBlocks: string[] = [];
  let productCount = 0;
  let blogPostCount = 0;

  // 1. Products grouped per Category
  if (includeProducts) {
    activeCategories.forEach(cat => {
      const catSlug = (cat.slug || cat.id).toLowerCase();
      if (
        categoryFilter !== 'all' &&
        categoryFilter !== cat.id.toLowerCase() &&
        categoryFilter !== catSlug
      ) {
        return;
      }

      const catProducts = activeProducts.filter(
        p =>
          p.category.toLowerCase() === cat.id.toLowerCase() ||
          p.category.toLowerCase() === catSlug
      );

      categoriesWithProducts.push({
        id: cat.id,
        name: cat.name,
        slug: cat.slug || cat.id,
        count: catProducts.length
      });

      if (catProducts.length > 0) {
        itemBlocks.push(
          `\n    <!-- ==================== CATEGORY: ${escapeXml(cat.name.toUpperCase())} (${catProducts.length} PRODUCTS) ==================== -->`
        );
      }

      catProducts.forEach(prod => {
        productCount++;
        const prodSlug = prod.slug || prod.id.toLowerCase();
        const prodUrl = `${cleanBase}/product/${prodSlug}`;
        const pubDate = toRfc822Date(prod.updatedAt || prod.createdAt);

        const imgUrl =
          prod.image && typeof prod.image === 'string' && !prod.image.startsWith('data:image/')
            ? prod.image.startsWith('http')
              ? prod.image
              : `${cleanBase}${prod.image.startsWith('/') ? '' : '/'}${prod.image}`
            : `${cleanBase}/hero-image-coveristic.webp`;

        const cleanDesc = stripHtmlTags(
          prod.description ||
            `Custom printed ${prod.title} in ${cat.name} by Coveristic Lebanon. Price: $${prod.price.toFixed(2)} USD. Express delivery across Lebanon.`
        );

        const priceFormatted = `${prod.price.toFixed(2)} USD`;
        const availability = prod.inStock !== false ? 'in stock' : 'out of stock';

        itemBlocks.push(`    <item>
      <title>${escapeXml(`${prod.title} - $${prod.price.toFixed(2)}`)}</title>
      <link>${escapeXml(prodUrl)}</link>
      <guid isPermaLink="true">${escapeXml(prodUrl)}</guid>
      <pubDate>${pubDate}</pubDate>
      <category domain="${escapeXml(`${cleanBase}/category/${cat.slug || cat.id}`)}">${escapeXml(cat.name)}</category>
      <description>${escapeXml(`[${cat.name}] $${prod.price.toFixed(2)} USD — ${cleanDesc}`)}</description>
      <g:id>${escapeXml(prod.id)}</g:id>
      <g:price>${escapeXml(priceFormatted)}</g:price>
      <g:availability>${escapeXml(availability)}</g:availability>
      <g:product_type>${escapeXml(cat.name)}</g:product_type>
      <media:content url="${escapeXml(imgUrl)}" medium="image">
        <media:title>${escapeXml(prod.title)}</media:title>
      </media:content>
    </item>`);
      });
    });

    // Include any uncategorized active products if filter is 'all'
    if (categoryFilter === 'all') {
      const knownCatIds = new Set(
        activeCategories.flatMap(c => [c.id.toLowerCase(), (c.slug || c.id).toLowerCase()])
      );
      const uncategorized = activeProducts.filter(
        p => !knownCatIds.has((p.category || '').toLowerCase())
      );
      if (uncategorized.length > 0) {
        itemBlocks.push(`\n    <!-- ==================== OTHER PRODUCTS (${uncategorized.length}) ==================== -->`);
        uncategorized.forEach(prod => {
          productCount++;
          const prodSlug = prod.slug || prod.id.toLowerCase();
          const prodUrl = `${cleanBase}/product/${prodSlug}`;
          const pubDate = toRfc822Date(prod.updatedAt || prod.createdAt);
          const cleanDesc = stripHtmlTags(prod.description || prod.title);
          itemBlocks.push(`    <item>
      <title>${escapeXml(`${prod.title} - $${prod.price.toFixed(2)}`)}</title>
      <link>${escapeXml(prodUrl)}</link>
      <guid isPermaLink="true">${escapeXml(prodUrl)}</guid>
      <pubDate>${pubDate}</pubDate>
      <category>${escapeXml(prod.category || 'Custom Print')}</category>
      <description>${escapeXml(cleanDesc)}</description>
    </item>`);
        });
      }
    }
  }

  // 2. Published Blog Posts
  if (includeBlogPosts && publishedBlogs.length > 0) {
    itemBlocks.push(
      `\n    <!-- ==================== BLOG POSTS & ARTICLES (${publishedBlogs.length} POSTS) ==================== -->`
    );

    publishedBlogs.forEach(article => {
      blogPostCount++;
      const articleUrl = `${cleanBase}/blog/${article.slug}`;
      const pubDate = toRfc822Date(article.updatedAt || article.publishedAt);
      const cleanExcerpt = stripHtmlTags(article.excerpt || article.title);

      const imgUrl =
        article.featuredImage &&
        typeof article.featuredImage === 'string' &&
        !article.featuredImage.startsWith('data:image/')
          ? article.featuredImage.startsWith('http')
            ? article.featuredImage
            : `${cleanBase}${article.featuredImage.startsWith('/') ? '' : '/'}${article.featuredImage}`
          : `${cleanBase}/hero-image-coveristic.webp`;

      itemBlocks.push(`    <item>
      <title>${escapeXml(article.title)}</title>
      <link>${escapeXml(articleUrl)}</link>
      <guid isPermaLink="true">${escapeXml(articleUrl)}</guid>
      <pubDate>${pubDate}</pubDate>
      <category domain="${escapeXml(`${cleanBase}/blog`)}">${escapeXml(`Blog${article.city ? ` - ${article.city}` : ''}`)}</category>
      <dc:creator>${escapeXml(article.author || 'Coveristic Editorial Studio')}</dc:creator>
      <description>${escapeXml(cleanExcerpt)}</description>
      <media:content url="${escapeXml(imgUrl)}" medium="image">
        <media:title>${escapeXml(article.title)}</media:title>
      </media:content>
    </item>`);
    });
  }

  const feedTitle =
    categoryFilter === 'blog'
      ? 'Coveristic Lebanon — Official Blog & Print Guides RSS Feed'
      : categoryFilter !== 'all'
      ? `Coveristic Lebanon — ${
          activeCategories.find(
            c => c.id.toLowerCase() === categoryFilter || (c.slug || '').toLowerCase() === categoryFilter
          )?.name || categoryFilter
        } RSS Feed`
      : 'Coveristic Lebanon — Complete Products per Category & Blog RSS Feed';

  const selfFeedUrl =
    categoryFilter === 'all'
      ? `${cleanBase}/rss.xml`
      : `${cleanBase}/rss?category=${encodeURIComponent(categoryFilter)}`;

  const xml = `<?xml version="1.0" encoding="UTF-8"?>
<rss version="2.0"
     xmlns:atom="http://www.w3.org/2005/Atom"
     xmlns:media="http://search.yahoo.com/mrss/"
     xmlns:dc="http://purl.org/dc/elements/1.1/"
     xmlns:g="http://base.google.com/ns/1.0">
  <channel>
    <title>${escapeXml(feedTitle)}</title>
    <link>${escapeXml(`${cleanBase}/rss`)}</link>
    <atom:link href="${escapeXml(selfFeedUrl)}" rel="self" type="application/rss+xml" />
    <description>${escapeXml(
      'Official RSS 2.0 syndication feed for Coveristic Lebanon custom print-on-demand products organized by category (Phone Cases, Canvas, Photo Frames, Wood Crafts, Keychains, Apparel, Mugs, Stickers) and blog articles.'
    )}</description>
    <language>en-lb</language>
    <lastBuildDate>${nowRfc}</lastBuildDate>
    <generator>Coveristic RSS Engine v2.0</generator>
    <ttl>60</ttl>${itemBlocks.join('\n')}
  </channel>
</rss>
`;

  const stats: RssSyncStats = {
    totalItems: productCount + blogPostCount,
    productCount,
    categoryCount: activeCategories.length,
    blogPostCount,
    categoriesWithProducts
  };

  const healthChecks: RssHealthCheck[] = [
    {
      id: 'xml-declaration',
      label: 'RSS 2.0 & XML Declaration Valid',
      passed: xml.startsWith('<?xml version="1.0"') && xml.includes('<rss version="2.0"'),
      detail: 'Valid UTF-8 XML header with Atom, Media RSS, and Google Merchant namespaces'
    },
    {
      id: 'categories-indexed',
      label: 'All Active Product Categories Indexed',
      passed: activeCategories.length > 0,
      detail: `${activeCategories.length} active categories structured in feed`
    },
    {
      id: 'products-indexed',
      label: 'Products Per Category Populated',
      passed: productCount > 0,
      detail: `${productCount} active listed products with pricing & image enclosures`
    },
    {
      id: 'blogs-indexed',
      label: 'Published Blog Posts Included',
      passed: blogPostCount > 0,
      detail: `${blogPostCount} published blog articles with permalinks & excerpts`
    },
    {
      id: 'permalinks-valid',
      label: 'Canonical Permalinks & GUIDs Verified',
      passed: cleanBase.startsWith('http://') || cleanBase.startsWith('https://'),
      detail: `All <link> and <guid> tags resolve to ${cleanBase}`
    }
  ];

  return { xml, stats, healthChecks };
}

/**
 * Generates a dedicated Arabic RSS 2.0 XML feed (/rss-ar.xml) with Arabic titles, descriptions, categories, and Arabic permalinks (/ar/product/...)
 */
export function generateArabicRssXml(
  products: Product[],
  categories: Category[],
  blogArticles: BlogArticle[],
  options: RssGenerationOptions = {}
): { xml: string; stats: RssSyncStats } {
  const cleanBase = (options.baseUrl || 'https://coveristic.net').trim().replace(/\/+$/, '');
  const categoryFilter = (options.categoryFilter || 'all').toLowerCase();
  const includeProducts = options.includeProducts !== false && categoryFilter !== 'blog';
  const includeBlogPosts =
    options.includeBlogPosts !== false && (categoryFilter === 'all' || categoryFilter === 'blog');

  const nowRfc = new Date().toUTCString();
  const activeCategories = (categories || []).filter(c => c && c.active !== false);
  const activeProducts = (products || []).filter(p => p && !p.inRecycleBin && !p.unlisted);
  const publishedBlogs = (blogArticles || []).filter(b => b && b.status === 'published');

  const categoriesWithProducts: RssSyncStats['categoriesWithProducts'] = [];
  const itemBlocks: string[] = [];
  let productCount = 0;
  let blogPostCount = 0;

  if (includeProducts) {
    activeCategories.forEach(cat => {
      const catSlug = (cat.slug || cat.id).toLowerCase();
      if (
        categoryFilter !== 'all' &&
        categoryFilter !== cat.id.toLowerCase() &&
        categoryFilter !== catSlug
      ) {
        return;
      }

      const catProducts = activeProducts.filter(
        p =>
          p.category.toLowerCase() === cat.id.toLowerCase() ||
          p.category.toLowerCase() === catSlug
      );

      const catNameAr = cat.nameAr || getCategoryLocalizedName(cat.id, 'ar');
      const catSlugAr = cat.slugAr || getCategoryArabicSlug(cat.id);

      categoriesWithProducts.push({
        id: cat.id,
        name: catNameAr,
        slug: catSlugAr,
        count: catProducts.length
      });

      if (catProducts.length > 0) {
        itemBlocks.push(
          `\n    <!-- ==================== القسم: ${escapeXml(catNameAr)} (${catProducts.length} منتج) ==================== -->`
        );
      }

      catProducts.forEach(prod => {
        productCount++;
        const prodTitleAr = getProductLocalizedTitle(prod, 'ar');
        const prodDescAr = stripHtmlTags(getProductLocalizedDescription(prod, 'ar'));
        const prodSlugAr = getProductArabicSlug(prod);
        const prodUrl = `${cleanBase}/ar/product/${prodSlugAr}`;
        const pubDate = toRfc822Date(prod.updatedAt || prod.createdAt);

        const imgUrl =
          prod.image && typeof prod.image === 'string' && !prod.image.startsWith('data:image/')
            ? prod.image.startsWith('http')
              ? prod.image
              : `${cleanBase}${prod.image.startsWith('/') ? '' : '/'}${prod.image}`
            : `${cleanBase}/hero-image-coveristic.webp`;

        const priceFormatted = `${prod.price.toFixed(2)} USD`;
        const availability = prod.inStock !== false ? 'in stock' : 'out of stock';

        itemBlocks.push(`    <item>
      <title>${escapeXml(`${prodTitleAr} - $${prod.price.toFixed(2)}`)}</title>
      <link>${escapeXml(prodUrl)}</link>
      <guid isPermaLink="true">${escapeXml(prodUrl)}</guid>
      <pubDate>${pubDate}</pubDate>
      <category domain="${escapeXml(`${cleanBase}/ar/category/${catSlugAr}`)}">${escapeXml(catNameAr)}</category>
      <description>${escapeXml(`[${catNameAr}] $${prod.price.toFixed(2)} USD — ${prodDescAr}`)}</description>
      <g:id>${escapeXml(`${prod.id}-ar`)}</g:id>
      <g:price>${escapeXml(priceFormatted)}</g:price>
      <g:availability>${escapeXml(availability)}</g:availability>
      <g:product_type>${escapeXml(catNameAr)}</g:product_type>
      <media:content url="${escapeXml(imgUrl)}" medium="image">
        <media:title>${escapeXml(prodTitleAr)}</media:title>
      </media:content>
    </item>`);
      });
    });
  }

  if (includeBlogPosts && publishedBlogs.length > 0) {
    itemBlocks.push(
      `\n    <!-- ==================== مقالات المدونة (${publishedBlogs.length}) ==================== -->`
    );

    publishedBlogs.forEach(article => {
      blogPostCount++;
      const articleUrl = `${cleanBase}/ar/blog/${article.slug}`;
      const pubDate = toRfc822Date(article.updatedAt || article.publishedAt);
      const cleanExcerpt = stripHtmlTags(article.excerpt || article.title);

      itemBlocks.push(`    <item>
      <title>${escapeXml(article.title)}</title>
      <link>${escapeXml(articleUrl)}</link>
      <guid isPermaLink="true">${escapeXml(articleUrl)}</guid>
      <pubDate>${pubDate}</pubDate>
      <category domain="${escapeXml(`${cleanBase}/ar/blog`)}">المدونة</category>
      <description>${escapeXml(cleanExcerpt)}</description>
    </item>`);
    });
  }

  const xml = `<?xml version="1.0" encoding="UTF-8"?>
<rss version="2.0"
     xmlns:atom="http://www.w3.org/2005/Atom"
     xmlns:media="http://search.yahoo.com/mrss/"
     xmlns:dc="http://purl.org/dc/elements/1.1/"
     xmlns:g="http://base.google.com/ns/1.0">
  <channel>
    <title>${escapeXml('كوفريستيك لبنان (Coveristic) — خلاصة RSS الرسمية للمنتجات المخصصة باللغة العربية (/ar/)')}</title>
    <link>${escapeXml(`${cleanBase}/ar/rss`)}</link>
    <atom:link href="${escapeXml(`${cleanBase}/rss-ar.xml`)}" rel="self" type="application/rss+xml" />
    <description>${escapeXml(
      'خلاصة RSS 2.0 الرسمية لمنتجات الطباعة حسب الطلب في لبنان من كوفريستيك باللغة العربية مع الروابط العربية الدائمة (كفرات هواتف، لوحات كانفاس، إطارات صور، محفورات خشبية، ميداليات، تيشيرتات، أكواب، وملصقات).'
    )}</description>
    <language>ar-lb</language>
    <lastBuildDate>${nowRfc}</lastBuildDate>
    <generator>Coveristic Arabic RSS Engine v2.0</generator>
    <ttl>60</ttl>${itemBlocks.join('\n')}
  </channel>
</rss>
`;

  const stats: RssSyncStats = {
    totalItems: productCount + blogPostCount,
    productCount,
    categoryCount: activeCategories.length,
    blogPostCount,
    categoriesWithProducts
  };

  return { xml, stats };
}

/**
 * Regenerates and syncs rss.xml and rss-ar.xml to localStorage and the hosting server (/api/rss.php)
 */
export async function syncRssToServer(
  products: Product[],
  categories: Category[],
  blogArticles: BlogArticle[],
  baseUrl: string = 'https://coveristic.net'
): Promise<RssSyncResult> {
  const cleanBase = baseUrl.trim().replace(/\/+$/, '');
  const { xml, stats, healthChecks } = generateRssXml(products, categories, blogArticles, {
    baseUrl: cleanBase,
    categoryFilter: 'all',
    includeProducts: true,
    includeBlogPosts: true
  });
  const { xml: xmlAr } = generateArabicRssXml(products, categories, blogArticles, {
    baseUrl: cleanBase,
    categoryFilter: 'all',
    includeProducts: true,
    includeBlogPosts: true
  });
  const timestamp = new Date().toISOString();

  if (typeof window !== 'undefined') {
    try {
      localStorage.setItem('coveristic_last_rss_xml', xml);
      localStorage.setItem('coveristic_last_rss_ar_xml', xmlAr);
      localStorage.setItem('coveristic_rss_updated_at', timestamp);
      localStorage.setItem('coveristic_rss_stats', JSON.stringify(stats));
      localStorage.setItem('coveristic_rss_base_url', cleanBase);
    } catch {}
  }

  let serverWrittenBytes = 0;
  try {
    const res = await fetch('/api/rss.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ xml, xmlAr, timestamp })
    });
    if (res.ok) {
      const data = await res.json();
      serverWrittenBytes = data.bytes || new Blob([xml]).size;
    }
  } catch (e) {
    // Fallback to local storage in SPA preview mode
    console.warn('RSS server sync fallback:', e);
  }

  return {
    success: true,
    message: 'English & Arabic RSS feeds (rss.xml & rss-ar.xml) regenerated and verified',
    bytes: serverWrittenBytes || new Blob([xml]).size,
    timestamp,
    xml,
    xmlAr,
    feedPageUrl: `${cleanBase}/rss`,
    feedXmlUrl: `${cleanBase}/rss.xml`,
    feedXmlArUrl: `${cleanBase}/rss-ar.xml`,
    healthChecks,
    ...stats
  };
}

